/**
 * Phase 302 — Kiosk Onboard Landing
 *
 * Lands at /onboard?token=<JWT>. Verifies the signed kiosk token via the
 * PUBLIC endpoint (404 for invalid), greets the parent with the academy
 * name, and hands them off to the WhatsApp conversational state machine
 * with a clear single-action CTA.
 *
 * Auth: NONE. The signed JWT in the URL is the sole credential.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, AlertTriangle, Loader2, MessageCircle } from 'lucide-react';

const PUBLIC_API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: PUBLIC_API_BASE, timeout: 15000 });

export default function KioskOnboardLanding() {
  const [params] = useSearchParams();
  const token = (params.get('token') || '').trim();
  const [meta, setMeta] = useState(null);   // null = loading, then {academy_name, phone_last4}
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!token) { setErr('No token provided.'); return; }
    let cancelled = false;
    publicApi.get(`/intake/onboard/${token}`)
      .then((r) => { if (!cancelled) setMeta(r.data); })
      .catch((e) => {
        if (cancelled) return;
        setErr(e?.response?.data?.detail || 'This onboarding link is no longer valid.');
      });
    return () => { cancelled = true; };
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-6 text-center" data-testid="onboard-token-error">
        <AlertTriangle className="h-14 w-14 text-rose-500 mb-4" />
        <h1 className="text-xl font-bold text-slate-900 mb-1">Link no longer valid</h1>
        <p className="text-sm text-slate-600 max-w-sm">{String(err)}</p>
        <p className="text-xs text-slate-400 mt-8">Out-Play.in · Onboarding</p>
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Verifying link…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex flex-col" data-testid="onboard-landing">
      <header className="px-6 py-3 text-center">
        <p className="text-xs font-semibold tracking-widest text-emerald-700">OUT-PLAY · ONBOARDING</p>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 max-w-md mx-auto text-center">
        <div className="mx-auto w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
          <CheckCircle2 className="h-14 w-14 text-emerald-600" strokeWidth={2.5} />
        </div>
        <h1 className="text-3xl font-display font-bold text-slate-900 mb-2" data-testid="onboard-headline">
          You&apos;re all set!
        </h1>
        <p className="text-base text-slate-700 leading-relaxed">
          Welcome to <span className="font-semibold text-emerald-700" data-testid="onboard-academy-name">{meta.academy_name}</span>.
        </p>

        {/* Hand-off CTA */}
        <div className="mt-8 rounded-2xl bg-white border-2 border-emerald-200 p-5 shadow-sm w-full" data-testid="onboard-handoff-card">
          <div className="flex items-start gap-3 text-left">
            <div className="shrink-0 w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <MessageCircle className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900 mb-1">Next step: reply on WhatsApp</p>
              <p className="text-sm text-slate-600 leading-relaxed">
                Open the WhatsApp message we just sent you and reply with the words
                <span className="inline-block bg-emerald-100 text-emerald-800 font-mono px-2 py-0.5 rounded mx-1 text-xs">Start Registration</span>
                to begin. We&apos;ll guide you step-by-step.
              </p>
            </div>
          </div>
        </div>

        <p className="text-[11px] text-slate-400 mt-10">
          Verification valid for 24 hours · Number ending in <span className="font-mono">{meta.phone_last4 || '••••'}</span>
        </p>
      </main>
    </div>
  );
}
